import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { UnifiedOpportunityDetail } from "@/components/opportunities/UnifiedOpportunityDetail";

// Wave 6 cleanup (2026-09-30) — old shared link for a single External Opportunity. Used to
// mount the pre-Unified `BuildOpportunitiesTab` browse-and-apply stack (kind="application"
// Interests), now fully retired. Still must resolve (old links stay shareable), so this fetches
// the one opportunity directly and renders it through the SAME `UnifiedOpportunityDetail` the
// current /opportunities tab uses — "Ask NJM to help" only, no flat Apply flow. A
// deleted/unpublished/expired id (or `404` because it's now hidden behind an open NJM
// Collaboration — see build_opportunities_public.py's `_is_hidden_by_promotion`) shows the
// friendly "no longer available" state instead of crashing.
export default function BuildOpportunityDeepLink() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [opportunity, setOpportunity] = useState(undefined);

  useEffect(() => {
    // Reuses the SAME unified list + shape `/opportunities` (the tab this app actually uses)
    // already relies on — guarantees `UnifiedOpportunityDetail` gets fields in the shape it
    // expects (summary/organisation/detail_route/source_url_valid/...), not the differently-
    // shaped `serialize_public()` the old build-opportunities-public single-GET returns.
    api.get("/v1/opportunities").then((r) => {
      const found = (r.data.items || []).find((i) => i.id === id && i.type === "external_opportunity");
      setOpportunity(found || null);
    }).catch(() => setOpportunity(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleSave = async (o) => {
    try {
      if (o.is_saved) await api.delete(`/v1/build-opportunities-public/${o.id}/save`);
      else await api.post(`/v1/build-opportunities-public/${o.id}/save`);
      setOpportunity((prev) => (prev ? { ...prev, is_saved: !prev.is_saved } : prev));
    } catch {
      toast.error("Couldn't update saved status");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-8 py-6 sm:py-10" data-testid="build-opportunity-deep-link-page">
      {opportunity === undefined ? (
        <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-faint" /></div>
      ) : opportunity === null ? (
        <EmptyState title="This opportunity is no longer available" hint="It may have closed, expired, or been taken down." testId="build-opportunity-deep-link-not-found" />
      ) : (
        <UnifiedOpportunityDetail opportunity={opportunity} onClose={() => navigate("/build/me")} onToggleSave={toggleSave} onSent={() => {}} />
      )}
    </div>
  );
}
